import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, RefreshCw, Settings2 } from 'lucide-react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { Button } from '../../../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import { useEnumValues } from '../../hooks/useEnumValues';
import { useMetaInfo } from '../../hooks/useMetaInfo';
import { openEnumDetailsDialog } from '../meta-info/enumDetailsDialogStore';
import { findWithSchemaFold } from '../../utils/metaInfoSchemaMatch';
import { useAnalytics } from '../../../../../core/analytics';
import { useLookupEnumLabelColumn } from './useLookupEnumLabelColumn';
import { matchPickedToListed } from './enumPickerSelection';
import { EnumPickerLookupFooter } from './EnumPickerLookupFooter';
import { resolveEffectiveLookupDeclaration } from '../../utils/effectiveLookupDeclaration';
import { FkLookupPicker } from '../../../table-explorer/components/FkLookupPicker';
import type { EnumLookupTarget } from '../../../../../types/electron';
import type { FilterGroup } from '../../../core/types/workspaceTypes';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';

export type EnumPickerOperator = 'eq' | 'neq' | 'in';

type EnumValuePickerProps = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  engine: string;
  operator: EnumPickerOperator;
  /** Current value. String for eq/neq; comma-separated string or array for in.
   *  Accepts `Date` for compatibility with FilterCondition.value (coerced via
   *  String()). Accepts `(string | number | bigint)[]` because FilterCondition.value's
   *  array member is `(string | number | bigint)[]` — this picker's own
   *  string-based UI still only ever emits strings; numeric members are
   *  stringified on read. */
  value: string | number | bigint | boolean | Date | null | undefined | (string | number | bigint)[];
  onChange: (next: string | string[]) => void;
  /** When provided, used as the dropdown trigger ARIA label. */
  ariaLabel?: string;
  className?: string;
  /** JSONPath within the JSON column; absent for column-level enum pickers. */
  path?: string;
  pathKind?: 'scalar' | 'array';
  /**
   * The single unconditional FK target this column resolves to (real FK, or
   * one unfiltered loose FK) — enables the "Search rows in …" escape into the
   * full row picker. Resolved by `useEnumPickerSlot`; never set for JSON-path
   * pickers.
   */
  fkBrowseTarget?: EnumLookupTarget;
  /** The winning loose FK's `targetFilter`, so the row picker stays scoped. */
  fkBrowseTargetFilter?: FilterGroup[];
  /** Source column's data type — the row picker's source context. */
  sourceDataType?: string;
};

type PickerValue = string | number | bigint | boolean | Date | null | undefined | (string | number | bigint)[];

/** Parse 'in' operator's value (string-CSV or array) into a string[]. */
const valueToArray = (v: PickerValue): string[] => {
  if (Array.isArray(v)) return v.map(String);
  if (v === null || v === undefined) return [];
  return String(v)
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
};

const valueToString = (v: PickerValue): string => {
  if (Array.isArray(v)) return v.join(', ');
  if (v === null || v === undefined) return '';
  return String(v);
};

export function EnumValuePicker({
  connectionId,
  database,
  schema,
  table,
  column,
  engine,
  operator,
  value,
  onChange,
  ariaLabel,
  className,
  path,
  pathKind,
  fkBrowseTarget,
  fkBrowseTargetFilter,
  sourceDataType,
}: EnumValuePickerProps) {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [search, setSearch] = useState('');
  const [browseOpen, setBrowseOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { track } = useAnalytics();

  // Look up the column's enum declaration (if any) so we can offer a settings
  // gear into the details dialog — the picker is also usable on eligible-but-
  // undeclared columns where opening "details" would be meaningless.
  const { data: metaInfo } = useMetaInfo(connectionId, database);
  const declared = useMemo(() => {
    if (!metaInfo) return undefined;
    return findWithSchemaFold(
      metaInfo.enumColumns,
      schema,
      (d) =>
        d.table === table &&
        d.column === column &&
        (d.path ?? undefined) === (path ?? undefined),
    );
  }, [metaInfo, schema, table, column, path]);

  // Lookup enums are a COLUMN-level concept: labels come from a referenced
  // table, which a JSON sub-key never has. `path` present ⇒ plain enum picker,
  // byte-for-byte the pre-lookup behaviour.
  //
  // Resolved through the shared seam so an IMPLICIT lookup enum — no
  // declaration, but `fkBrowseTarget`'s table is flagged in `referenceTables` —
  // fetches the same pairs, with the same live label column, as an explicit
  // one. `fkBrowseTarget` is exactly `resolveLookupEnumTarget`'s output
  // (resolved by `useEnumPickerSlot`), which is what makes implicitness
  // decidable here.
  const effectiveLookup = useMemo(() => {
    if (path) return null;
    return resolveEffectiveLookupDeclaration({
      metaInfo,
      schema,
      table,
      column,
      resolvedTarget: fkBrowseTarget,
    });
  }, [path, metaInfo, schema, table, column, fkBrowseTarget]);

  const lookupTarget: EnumLookupTarget | undefined =
    effectiveLookup === null
      ? undefined
      : effectiveLookup?.kind !== 'explicit'
        ? effectiveLookup.lookup
        : effectiveLookup?.declaration.source === 'lookup'
          ? effectiveLookup.declaration.lookup
          : undefined;

  /** An implicit column has no declaration — the gear still opens details. */
  const isImplicitLookup = effectiveLookup?.kind === 'implicit';

  // Cached pairs for this column — read straight off the MetaInfo snapshot for
  // the footer's "refreshed …" stamp (the values themselves come from
  // `useEnumValues`, which seeds from this same entry).
  const cacheEntry = useMemo(() => {
    if (!metaInfo) return undefined;
    return metaInfo.enumValueCache?.find(
      (e) =>
        e.schema === schema &&
        e.table === table &&
        e.column === column &&
        (e.path ?? undefined) === (path ?? undefined),
    );
  }, [metaInfo, schema, table, column, path]);

  // The label column is NOT stored on the declaration — it's the shared, live
  // `tableDisplayColumns` entry for the target table, so a change there takes
  // effect on the next refresh. Resolving it costs a `getTableMetadata` call
  // on the TARGET table, so the hook is armed only while this popover is open
  // on a lookup-declared column (no target ⇒ the hook stays inert).
  const labelColumnState = useLookupEnumLabelColumn({
    connectionId,
    database,
    target: open && lookupTarget ? lookupTarget : undefined,
  });

  // A cold lookup column must not fetch before its label column resolves —
  // that would cache keys with no labels until a manual refresh. Gates both
  // the lazy first fetch (below) and the hook's background revalidation.
  const lookupLabelPending = !!lookupTarget && !labelColumnState.ready;

  const hook = useEnumValues({
    connectionId,
    database,
    schema,
    table,
    column,
    engine,
    enabled,
    // Open popover = the user is looking at these values right now, which is
    // what lets a stale LOOKUP cache revalidate in the background (Task 5.1).
    // `enabled` can't stand in for it: it is armed only on a cache miss.
    active: open && !lookupLabelPending,
    path,
    pathKind,
    // A lookup column must refresh from its referenced table — `'auto'` would
    // re-sample the key column itself and throw away the labels.
    ...(lookupTarget
      ? {
          source: 'lookup' as const,
          lookup: {
            toSchema: lookupTarget.toSchema,
            toTable: lookupTarget.toTable,
            toColumn: lookupTarget.toColumn,
            labelColumn: labelColumnState.labelColumn,
          },
        }
      : {}),
  });

  // key → label for the rows and the trigger summary. Undefined for every
  // non-lookup source, which keeps their rendering untouched.
  const labelByKey = useMemo(() => {
    if (path || !hook.entries) return undefined;
    const map = new Map<string, string>();
    for (const entry of hook.entries) {
      if (entry.label !== null && entry.label !== '') map.set(entry.value, entry.label);
    }
    return map.size > 0 ? map : undefined;
  }, [path, hook.entries]);

  const labelFor = useCallback(
    (key: string): string | null => labelByKey?.get(key) ?? null,
    [labelByKey],
  );

  /** `Active ·3` — label prominent, key muted. Bare key when unlabelled. */
  const renderKeyWithLabel = useCallback(
    (key: string) => {
      const label = labelFor(key);
      if (label === null) return key;
      return (
        <>
          {label}
          <span className="text-muted-foreground">{` ·${key}`}</span>
        </>
      );
    },
    [labelFor],
  );

  // The committed value as the LISTED values it stands for — a date read back
  // from SQL (`TO_DATE(…)` → `2024-01-15T00:00:00`) is the sampled
  // `2024-01-15T00:00:00.000Z` it was picked as (`enumPickerSelection.ts`).
  const listedValues = hook.values;
  const selectedArray = useMemo(
    () => matchPickedToListed(valueToArray(value), listedValues, engine as DatabaseEngineId, sourceDataType),
    [value, listedValues, engine, sourceDataType],
  );
  const rawSingle = valueToString(Array.isArray(value) ? '' : value);
  const selectedSingle = useMemo(
    () => (rawSingle ? matchPickedToListed([rawSingle], listedValues, engine as DatabaseEngineId, sourceDataType)[0]! : ''),
    [rawSingle, listedValues, engine, sourceDataType],
  );

  // multiMode: true when operator is 'in' (starts in multi) or user opts in via footer link.
  const [multiMode, setMultiMode] = useState(operator === 'in');
  // pending: local selection in multi mode. Seeded from props at mount; reset
  // to props on discard-close (Popover onOpenChange); re-seeded on mode-switch
  // via the "Select multiple…" footer link.
  const [pending, setPending] = useState<string[]>(
    operator === 'in' ? [...selectedArray] : [],
  );
  // Values load after mount (the list opens lazily): a pending read-back value
  // becomes its listed twin once the list is there, so it shows checked and a
  // click on it deselects it instead of adding a duplicate.
  useEffect(() => {
    setPending((prev) => {
      const next = matchPickedToListed(prev, listedValues, engine as DatabaseEngineId, sourceDataType);
      return next === prev ? prev : [...next];
    });
  }, [listedValues, engine, sourceDataType]);

  const isMulti = multiMode;

  const handleSelect = useCallback(
    (picked: string) => {
      if (isMulti) {
        // Pending only — do NOT call onChange, do NOT close.
        setPending((prev) => {
          if (prev.includes(picked)) {
            return prev.filter((v) => v !== picked);
          }
          return [...prev, picked];
        });
      } else {
        onChange(picked);
        setOpen(false);
      }
    },
    [isMulti, onChange],
  );

  const handleRefresh = useCallback(async () => {
    setEnabled(true);
    const result = await hook.refetch();
    if (result?.data) {
      track('feature:enum_values_refresh', {
        engine,
        source: result.data.source,
        on_json_path: !!path,
        // Distinguishes the user pressing Refresh from the background
        // refreshes (`miss` — `useLookupLabelSelfHeal`).
        reason: 'manual',
      });
    }
  }, [engine, hook, path, track]);

  // Browse-all-rows escape: pick any row of the referenced table, commit its
  // key through the normal pick path. The live resolution wins; a declared
  // lookup target is the fallback for when the loose declaration behind it has
  // since been removed. Only offered in single mode — in multi mode the
  // popover holds uncommitted `pending` state that closing would discard.
  const browseTarget = fkBrowseTarget ?? lookupTarget;
  const canBrowseRows = !path && !!browseTarget && !isMulti;

  const handleBrowseAll = useCallback(() => {
    track('feature:enum_picker_browse_rows', { engine });
    setSearch('');
    setOpen(false);
    setBrowseOpen(true);
  }, [engine, track]);

  // Trigger label is always based on committed props (selectedArray / selectedSingle),
  // not the local pending state, so it only updates after onChange is called.
  // A lookup column shows the cached label with the key kept visible (`Active
  // ·3`) — the stored value never changes, only how it reads.
  const triggerLabel =
    operator === 'in' || multiMode
      ? selectedArray.length === 0
        ? 'Pick values…'
        : selectedArray.length === 1
          ? renderKeyWithLabel(selectedArray[0])
          : `${selectedArray.length} values`
      : selectedSingle
        ? renderKeyWithLabel(selectedSingle)
        : 'Pick a value…';

  const isLoading = hook.isLoading || (enabled && hook.values.length === 0 && !hook.isError && !hook.truncated);
  const truncated = hook.truncated;

  // Lazy-load on first open when there's no cache entry yet. An effect rather
  // than an `onOpenChange` side-effect because a lookup fetch has to wait for
  // the target's label column to resolve — firing before it lands would cache
  // a keys-only result and hide the labels until a manual refresh.
  useEffect(() => {
    if (!open || enabled || hook.isCacheHit || lookupLabelPending) return;
    setEnabled(true);
  }, [open, enabled, hook.isCacheHit, lookupLabelPending]);

  return (
    <>
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setSearch('');
          // Discard pending changes if closed without Apply — reset to props.
          setMultiMode(operator === 'in');
          setPending(operator === 'in' ? [...selectedArray] : []);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel ?? 'Pick enum value'}
          data-testid="enum-value-picker-trigger"
          data-multi={isMulti ? 'true' : 'false'}
          className={`h-7 px-2 text-xs justify-between font-normal ${className ?? ''}`}
        >
          <span translate="no" className="truncate max-w-[180px]">
            {triggerLabel}
          </span>
          <ChevronDown className="h-3 w-3 ml-1 opacity-60" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align="start"
        data-testid="enum-value-picker-popover"
      >
        {/* Truncation warning — shared --energy hue (CH-09 §6 item 7), not a
            one-off amber. Hue only; the meaning is unchanged. */}
        {truncated && (
          <div
            className="rounded-md border-b border-energy bg-energy-soft p-2 text-xs"
            data-testid="enum-value-picker-truncated"
          >
            More than 200 distinct values —{' '}
            {path
              ? 'this JSON path probably isn’t an enum.'
              : 'this column probably isn’t an enum.'}{' '}
            Use a free-text filter instead.
          </div>
        )}
        <Command>
          <CommandInput
            placeholder="Search or type a value…"
            className="h-8 text-xs"
            value={search}
            onValueChange={setSearch}
          />
          {(() => {
            const trimmed = search.trim();
            // Predeclared values are a hint, not a constraint — let the user
            // filter by an ad-hoc value the sample/native set doesn't cover.
            const canCreate =
              trimmed.length > 0 && !hook.values.includes(trimmed);
            return (
              <CommandList>
                {isLoading ? (
                  <div
                    className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground"
                    data-testid="enum-value-picker-loading"
                  >
                    <RandomDotmSpinner size={12} />
                    Loading values…
                  </div>
                ) : (
                  <>
                    {hook.values.length === 0 && !canCreate && (
                      <CommandEmpty>
                        {hook.isError
                          ? 'Failed to load values.'
                          : enabled
                            ? 'No values found.'
                            : 'Open to load values.'}
                      </CommandEmpty>
                    )}
                    {hook.values.length > 0 && (
                      <CommandGroup>
                        {hook.values.map((v) => {
                          const isSelected = isMulti
                            ? pending.includes(v)
                            : selectedSingle === v;
                          // Lookup pairs: the label leads, the stored key stays
                          // visible (muted, right-aligned) so what lands in the
                          // filter is never a mystery. `keywords` makes cmdk
                          // match the label as well as the key.
                          const label = labelFor(v);
                          return (
                            <CommandItem
                              key={v}
                              value={v}
                              keywords={label ? [label] : undefined}
                              onSelect={() => handleSelect(v)}
                              className="text-xs"
                              data-testid="enum-value-picker-item"
                              data-enum-key={v}
                              data-selected={isSelected ? 'true' : 'false'}
                            >
                              <Check
                                className={`h-3 w-3 mr-2 ${isSelected ? 'opacity-100' : 'opacity-0'}`}
                                aria-hidden="true"
                              />
                              <span translate="no" className="truncate">
                                {label ?? v}
                              </span>
                              {label !== null && (
                                <span
                                  translate="no"
                                  className="ml-auto pl-2 shrink-0 font-mono text-[10px] text-muted-foreground"
                                  data-testid="enum-value-picker-item-key"
                                >
                                  {v}
                                </span>
                              )}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    )}
                    {canCreate && (
                      <CommandGroup>
                        <CommandItem
                          value={trimmed}
                          onSelect={() => {
                            handleSelect(trimmed);
                            setSearch('');
                          }}
                          className="text-xs"
                          data-testid="enum-value-picker-create"
                        >
                          <Check className="h-3 w-3 mr-2 opacity-0" aria-hidden="true" />
                          <span className="truncate">
                            Use{' '}
                            <span translate="no" className="font-medium">
                              “{trimmed}”
                            </span>
                          </span>
                        </CommandItem>
                      </CommandGroup>
                    )}
                  </>
                )}
              </CommandList>
            );
          })()}
          <div className="border-t p-1">
            {!isMulti && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs h-7"
                data-testid="enum-value-picker-multi-toggle"
                onClick={() => {
                  setPending(selectedSingle ? [selectedSingle] : [...selectedArray]);
                  setMultiMode(true);
                }}
              >
                Select multiple…
              </Button>
            )}
            {isMulti && (() => {
              // Membership-based, not length-based: a length collision can
              // happen when pending holds ad-hoc/out-of-list values, so use
              // a real subset check and preserve anything not in hook.values.
              const allListedSelected =
                hook.values.length > 0 &&
                hook.values.every((v) => pending.includes(v));
              return (
              <div className="flex items-center justify-between gap-2 px-1 pt-1 pb-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                  data-testid="enum-value-picker-select-all"
                  onClick={() =>
                    setPending(
                      allListedSelected
                        ? pending.filter((v) => !hook.values.includes(v))
                        : [
                            ...pending.filter((v) => !hook.values.includes(v)),
                            ...hook.values,
                          ],
                    )
                  }
                >
                  {allListedSelected ? 'Select none' : 'Select all'}
                </Button>
                <Button
                  size="sm"
                  className="text-xs h-7"
                  data-testid="enum-value-picker-apply"
                  disabled={pending.length === 0}
                  onClick={() => {
                    onChange(pending);
                    setOpen(false);
                  }}
                >
                  Apply ({pending.length})
                </Button>
              </div>
              );
            })()}
            <EnumPickerLookupFooter
              target={lookupTarget}
              sampledAt={cacheEntry?.sampledAt}
              canBrowse={canBrowseRows}
              browseTarget={browseTarget}
              onBrowseAll={handleBrowseAll}
            />
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 justify-start text-xs h-7"
                onClick={handleRefresh}
                disabled={hook.isLoading || lookupLabelPending}
                data-testid="enum-value-picker-refresh"
              >
                <RefreshCw
                  className={`h-3 w-3 mr-2 ${hook.isLoading ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
                Refresh values from data
              </Button>
              {(declared || isImplicitLookup) && (
                <button
                  type="button"
                  data-testid="enum-value-picker-column-details"
                  title={path ? 'Open enum path details' : 'Open enum column details'}
                  aria-label={path ? 'Open enum path details' : 'Open enum column details'}
                  onClick={(e) => {
                    e.stopPropagation();
                    openEnumDetailsDialog({
                      connectionId,
                      database,
                      schema,
                      table,
                      column,
                      engine: engine as DatabaseEngineId,
                      // An implicit column has no declaration record; `lookup`
                      // is the only source it can have (R5 gives the details
                      // dialog its implicit variant).
                      declaredSource: declared?.source ?? 'lookup',
                      source: 'enum-picker-footer',
                      ...(path !== undefined ? { path, pathKind } : {}),
                    });
                  }}
                  className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
                >
                  <Settings2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
    {/* Mounted OUTSIDE the popover (mirrors `FkCellEditor`): opening the row
        picker closes the popover, and a dialog inside it would unmount with
        it. Esc unwinds picker → (already-closed) popover, same order. */}
    {browseOpen && browseTarget && (
      <FkLookupPicker
        open
        onOpenChange={(o) => {
          if (!o) setBrowseOpen(false);
        }}
        connectionId={connectionId}
        database={database}
        engine={engine as DatabaseEngineId}
        referencedSchema={browseTarget.toSchema}
        referencedTable={browseTarget.toTable}
        referencedColumn={browseTarget.toColumn}
        sourceLabel={`Pick a ${column}`}
        source={{
          schema,
          table,
          column,
          dataType: sourceDataType ?? '',
        }}
        targetFilter={fkBrowseTargetFilter}
        // The target is already resolved (real FK, or the single unfiltered
        // loose declaration) — skip the picker's own multi-FK chooser.
        preselectedLooseRelId={browseTarget.relationshipId}
        returnFocusRef={triggerRef}
        // No relationship id = a real FK target (`EnumLookupTarget`), which
        // wins over any loose declaration on the column.
        referencesRealForeignKey={browseTarget.relationshipId === undefined}
        onSelect={(picked) => {
          setBrowseOpen(false);
          // Same commit path as a normal pick — the KEY, never the label.
          handleSelect(String(picked));
        }}
      />
    )}
    </>
  );
}
